import AppsIcon from '@mui/icons-material/Apps';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import React from 'react';
import dmdBlueLockup from '../../assets/dmd/dmd-blue-dmd.svg';
import { ReactComponent as IntegrityMark } from '../../assets/dmd/integrity-i.svg';
import {
  ALLIANCE_APP_ORDER,
  ALLIANCE_APPS,
  INTEGRITY_LINKS,
} from './alliance-apps';
import { APP_BAR } from './alliance-tokens';
import { APP_ICONS } from './app-icons';
import { BarButton, useIsMobileLayout } from './bar-controls';
import { BarMenu, MENU_COLORS, MenuOverline } from './bar-menu';
import { CURRENT_APP } from './current-app';

/**
 * A 40px app tile: the app's tile color (Dashboard navy, Contracting a gold
 * gradient) with its glyph in the tile's icon color. The same tile is used
 * on the Sign In page and the 404 screen.
 */
export function AppTile(props: { appKey: keyof typeof ALLIANCE_APPS }) {
  const app = ALLIANCE_APPS[props.appKey];
  const Icon = APP_ICONS[props.appKey];

  return (
    <Box
      sx={{
        width: APP_BAR.appTileSize,
        height: APP_BAR.appTileSize,
        flex: 'none',
        borderRadius: `${APP_BAR.appTileRadius}px`,
        background: app.tile.bg,
        border: '1px solid #ffffff1a',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: app.tile.icon,
      }}
    >
      <Icon sx={{ fontSize: 22 }} />
    </Box>
  );
}

/**
 * App Launcher: the grid button at the far right of every App Bar. Lists the
 * two DMD apps (the current one marked "You’re here"), then a collapsible
 * Integrity group. It never lists the Alliance apps. Other apps and Integrity links open in a new
 * tab. Nothing in here is per app.
 */
export function AppLauncher() {
  // State
  const [open, setOpen] = React.useState(false);
  const [integrityOpen, setIntegrityOpen] = React.useState(false);
  // Hooks
  const isMobile = useIsMobileLayout();

  const handleClose = () => {
    setOpen(false);
    setIntegrityOpen(false);
  };

  const rowPadding = isMobile ? '12px 10px' : '10px';

  return (
    <React.Fragment>
      <BarButton
        square
        open={open}
        aria-label="DMD apps"
        title="DMD apps"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'dmd-app-launcher' : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        <AppsIcon sx={{ fontSize: 22 }} />
      </BarButton>

      <BarMenu
        id="dmd-app-launcher"
        label="DMD apps"
        open={open}
        onClose={handleClose}
        width={APP_BAR.launcherWidth}
      >
        <Box
          sx={{
            p: '14px 18px 6px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            component="img"
            src={dmdBlueLockup}
            alt="DMD"
            sx={{ display: 'block', height: 14, width: 59 }}
          />
          <MenuOverline>Apps</MenuOverline>
        </Box>

        <Box
          component="nav"
          sx={{
            p: '4px 8px 8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          {ALLIANCE_APP_ORDER.map((key) => {
            const app = ALLIANCE_APPS[key];
            const isCurrent = key === CURRENT_APP.key;
            return (
              <Box
                key={key}
                component={isCurrent ? 'div' : 'a'}
                href={isCurrent ? undefined : app.url}
                target={isCurrent ? undefined : '_blank'}
                rel={isCurrent ? undefined : 'noopener noreferrer'}
                aria-current={isCurrent ? 'page' : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  p: rowPadding,
                  borderRadius: '10px',
                  textDecoration: 'none',
                  color: 'inherit',
                  backgroundColor: isCurrent
                    ? MENU_COLORS.hover
                    : 'transparent',
                  cursor: isCurrent ? 'default' : 'pointer',
                  '&:hover': { backgroundColor: MENU_COLORS.hover },
                }}
              >
                <AppTile appKey={key} />
                <AppRowText title={app.name} description={app.description} />
                {isCurrent ? (
                  <Box
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '.08em',
                      textTransform: 'uppercase',
                      border: `1px solid ${MENU_COLORS.divider}`,
                      borderRadius: '6px',
                      p: '4px 8px',
                      color: MENU_COLORS.text2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    You’re here
                  </Box>
                ) : (
                  <OpenInNewIcon
                    sx={{ fontSize: 18, color: MENU_COLORS.text2 }}
                  />
                )}
              </Box>
            );
          })}

          <Box
            sx={{
              height: '1px',
              backgroundColor: MENU_COLORS.divider,
              m: '6px 10px',
            }}
          />

          <ButtonBase
            aria-expanded={integrityOpen}
            onClick={() => setIntegrityOpen((current) => !current)}
            sx={{
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              p: rowPadding,
              borderRadius: '10px',
              textAlign: 'left',
              backgroundColor: integrityOpen
                ? MENU_COLORS.hover
                : 'transparent',
              '&:hover': { backgroundColor: MENU_COLORS.hover },
            }}
          >
            <Box
              sx={{
                width: APP_BAR.appTileSize,
                height: APP_BAR.appTileSize,
                flex: 'none',
                borderRadius: `${APP_BAR.appTileRadius}px`,
                backgroundColor: '#0a2240',
                border: '1px solid #ffffff1a',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IntegrityMark height={20} width={15} aria-label="Integrity" />
            </Box>
            <AppRowText
              title="Integrity"
              description="Extra resources and tools"
            />
            {integrityOpen ? (
              <ExpandLessIcon sx={{ fontSize: 20, color: MENU_COLORS.text2 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20, color: MENU_COLORS.text2 }} />
            )}
          </ButtonBase>

          <Collapse in={integrityOpen} unmountOnExit>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                p: '2px 0 4px 54px',
              }}
            >
              {INTEGRITY_LINKS.map((link) => (
                <Box
                  key={link.name}
                  component="a"
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    p: '8px 10px',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:hover': { backgroundColor: MENU_COLORS.hover },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ fontSize: 14, fontWeight: 500 }}>
                      {link.name}
                    </Box>
                    <Box sx={{ fontSize: 12, color: MENU_COLORS.text2 }}>
                      {link.description}
                    </Box>
                  </Box>
                  <OpenInNewIcon
                    sx={{ fontSize: 16, color: MENU_COLORS.text2 }}
                  />
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>
      </BarMenu>
    </React.Fragment>
  );
}

/** Title + description column of a launcher row */
function AppRowText(props: { title: string; description: string }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
      }}
    >
      <Box sx={{ fontSize: 15, fontWeight: 500 }}>{props.title}</Box>
      <Box sx={{ fontSize: 13, color: MENU_COLORS.text2 }}>
        {props.description}
      </Box>
    </Box>
  );
}
